import React from "react";

interface MetroRow {
  metro: string;
  province: string;
  loans: string;
  balance: string;
  defaultRate: string;
  avgLTV: string;
  shareOfBook: string;
  band: "low" | "mid" | "high";
}

const metroData: MetroRow[] = [
  {
    metro: "Toronto CMA",
    province: "ON",
    loans: "1,136",
    balance: "$548M",
    defaultRate: "1.8%",
    avgLTV: "74%",
    shareOfBook: "27%",
    band: "low",
  },
  {
    metro: "Vancouver CMA",
    province: "BC",
    loans: "923",
    balance: "$496M",
    defaultRate: "2.1%",
    avgLTV: "78%",
    shareOfBook: "22%",
    band: "low",
  },
  {
    metro: "Montreal CMA",
    province: "QC",
    loans: "734",
    balance: "$318M",
    defaultRate: "2.3%",
    avgLTV: "76%",
    shareOfBook: "15%",
    band: "low",
  },
  {
    metro: "Calgary CMA",
    province: "AB",
    loans: "412",
    balance: "$174M",
    defaultRate: "3.4%",
    avgLTV: "79%",
    shareOfBook: "9%",
    band: "mid",
  },
  {
    metro: "Edmonton CMA",
    province: "AB",
    loans: "267",
    balance: "$123M",
    defaultRate: "3.7%",
    avgLTV: "80%",
    shareOfBook: "7%",
    band: "mid",
  },
  {
    metro: "Halifax CMA",
    province: "NS",
    loans: "112",
    balance: "$39M",
    defaultRate: "4.1%",
    avgLTV: "81%",
    shareOfBook: "3%",
    band: "high",
  },
];

const getBadgeClasses = (band: "low" | "mid" | "high") => {
  switch (band) {
    case "low":
      return "bg-[rgba(0,255,136,0.18)] text-neon-green border-[rgba(0,255,136,0.7)]";
    case "mid":
      return "bg-[rgba(255,184,0,0.18)] text-[#ffb800] border-[rgba(255,184,0,0.7)]";
    case "high":
      return "bg-[rgba(255,51,102,0.18)] text-[#ff3366] border-[rgba(255,51,102,0.7)]";
  }
};

export const MetroBreakdown: React.FC = () => {
  return (
    <div className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Metro breakdown
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Metro-level view of defaults, LTV and portfolio share (last 12M).
          </div>
        </div>
      </div>

      <div className="rounded-[12px] overflow-auto border border-[rgba(139,146,176,0.35)] bg-gradient-to-b from-[#1c2243] to-[#050716] max-h-[360px]">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-[1] bg-[rgba(5,7,22,0.95)] backdrop-blur-[8px]">
            <tr>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Metro
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Province
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Loans
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Balance
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Default rate
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Avg LTV
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Share of book
              </th>
              <th className="px-3.5 py-3 text-left text-[11px] uppercase tracking-[0.16em] text-[#8b92b0] border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                Band
              </th>
            </tr>
          </thead>
          <tbody>
            {metroData.map((row, idx) => (
              <tr
                key={idx}
                className="hover:bg-[rgba(0,212,255,0.08)] transition-colors"
              >
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.metro}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.province}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.loans}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.balance}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.defaultRate}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.avgLTV}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  {row.shareOfBook}
                </td>
                <td className="px-3.5 py-3 text-left border-b border-[rgba(139,146,176,0.14)] whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.12em] border ${getBadgeClasses(
                      row.band
                    )}`}
                  >
                    {row.band === "low"
                      ? "Low"
                      : row.band === "mid"
                      ? "Medium"
                      : "High"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-1.5 text-[11px] text-[#8b92b0] flex items-center justify-between gap-2 flex-wrap">
        <span>
          Showing <strong>6</strong> key metros · Drill-downs available via API
          and investor workspaces.
        </span>
        <span>Use API for full region cube · v2.0 schema</span>
      </div>
    </div>
  );
};
